import React, { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';
import {
  Container,
  Card,
  Row,
  Box,
  Flex,
  Button,
  Text,
} from '../../design-system';
import Alert from '../../helpers/alert';
import PaginatedTableWithSearch from '../../components/PaginatedTableWithSearch';
import {
  errorMsg,
  formatEIN,
  required,
  taxIDVal,
  checkPermissions,
  catchPlanDetailError,
} from '../../helpers/utils';
import ContainerHeader from '../../components/ContainerHeader';
import FieldColumn from '../../components/FieldColumn';
import ModalForm from '../../components/ModalForm';
import ExportButton from '../../components/ExportButton';
import { Session } from '../../contexts/sessionContext';
import {
  COUNTRY_OPTIONS,
  PERMISSIONS,
  STATE_OPTIONS,
} from '../../globalConstants';
import { FUNDERS_COLUMNS, FUNDER_TYPES } from './constants';

const openFunderLookup = (e: React.MouseEvent) => {
  e.preventDefault();
  const win = window.open(
    'https://projects.propublica.org/nonprofits/',
    '_blank',
  );
  win.focus();
};

const modalEINLayout = (values: any) => (
  <Container>
    <Row justifyContent="center">
      <Button variant="grey" type="button" onClick={openFunderLookup}>
        Find your funder's EIN here
      </Button>
    </Row>
    <Row>
      <FieldColumn
        name="tax_id"
        label="EIN"
        transformOnBlur={formatEIN}
        validate={taxIDVal}
      />
    </Row>
    <Row>
      <FieldColumn
        name="type"
        label="Type"
        component="select"
        options={FUNDER_TYPES}
      />
    </Row>
  </Container>
);

const modalLayout = (values: any) => (
  <Container>
    <Row>
      <FieldColumn name="name" label="Legal Name" validate={required} />
    </Row>
    <Row>
      <FieldColumn
        name="tax_id"
        label="EIN"
        transformOnBlur={formatEIN}
        validate={taxIDVal}
      />
      <FieldColumn
        name="type"
        label="Type"
        component="select"
        options={FUNDER_TYPES}
      />
    </Row>
    <Row>
      <FieldColumn name="address" label="Address" />
    </Row>
    <Row>
      <Flex>
        <FieldColumn name="city" label="City" />
        <Box width={250}>
          <FieldColumn
            name="state"
            label="State"
            component="select"
            options={values.country ? STATE_OPTIONS(values.country) : []}
          />
        </Box>
        <Box width={250}>
          <FieldColumn
            name="country"
            label="Country"
            component="select"
            options={COUNTRY_OPTIONS}
          />
        </Box>
      </Flex>
    </Row>
    <Row>
      <Box width={250}>
        <FieldColumn name="zip" label="Zip" />
      </Box>
    </Row>
  </Container>
);
const Funders = () => {
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useState(false);
  const [isEINModalOpen, toggleEINModal] = useState(false);
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);
  const canCreate = checkPermissions(permissions, [PERMISSIONS.CREATE_FUNDERS]);
  const canEdit = checkPermissions(permissions, [PERMISSIONS.UPDATE_FUNDERS]);

  // Store reference to fetchData from PaginatedTable for refreshing after operations
  const [tableFetchData, setTableFetchData] = useState<any>(null);

  const handleSubmit = async (values: any) => {
    try {
      const {
        data: { id },
      } = await axios.post('/funder', {
        ...values,
      });
      navigate(`/funders/edit/${id}`);
      Alert.success('Funder successfully created');

      // Refresh table data
      if (tableFetchData) {
        tableFetchData();
      }
    } catch (error) {
      catchPlanDetailError(error?.response);
    }
  };

  const handleEINSubmit = async (values: any) => {
    const tax_id = formatEIN(values.tax_id).replace('-', '');

    if (taxIDVal(tax_id) !== undefined) {
      Alert.error('Invalid Tax ID!');
      return;
    }
    try {
      const {
        data: { id },
      } = await axios.post(`/funder/generate/${tax_id}`, {
        ...values,
      });
      navigate(`/funders/edit/${id}`);
      Alert.success('Funder successfully created');

      // Refresh table data
      if (tableFetchData) {
        tableFetchData();
      }
    } catch (error) {
      catchPlanDetailError(error?.response);
    }
  };

  const handleEINChange = async (values: any) => {};

  // Stable identity so PaginatedTableWithSearch's onDataChange effect (which
  // lists onDataChange in its deps) does not re-fire → setState → re-render →
  // new callback → loop. The functional-updater form stores the fn value.
  const handleDataChange = useCallback(
    (data: any, fetchData: any, loading: any, error: any) => {
      setTableFetchData(() => fetchData);
    },
    [],
  );

  // Process columns to add canEdit permission to each row
  const processedColumns = FUNDERS_COLUMNS.map((column) => {
    if (column.accessor && typeof column.Cell === 'function') {
      return {
        ...column,
        Cell: (cellProps) => {
          const originalCell = column.Cell({
            ...cellProps,
            row: {
              ...cellProps.row,
              original: {
                ...cellProps.row.original,
                canEdit,
              },
            },
          });
          return originalCell;
        },
      };
    }
    return column;
  });

  return (
    <Container fluid gutter={50}>
      <Card mt={30}>
        <ContainerHeader>
          <Text fontWeight={700} fontSize={25}>
            Funders
          </Text>
          {canCreate && (
            <Button.Group>
              <Button type="button" onClick={() => toggleModal(true)}>
                Add
              </Button>
              <Button type="button" onClick={() => toggleEINModal(true)}>
                Automatic Add (Beta)
              </Button>
              <Box ml="1rem">
                <ExportButton type="funders" />
              </Box>
            </Button.Group>
          )}
          <ModalForm
            title="Add Funder Manually"
            isOpen={isModalOpen}
            onSubmit={handleSubmit}
            onClose={() => toggleModal(false)}
            render={modalLayout}
            initialValues={{ country: 'United States' }}
          />
          <ModalForm
            title="Add Funder by EIN"
            isOpen={isEINModalOpen}
            onSubmit={handleEINSubmit}
            onClose={() => toggleEINModal(false)}
            onChange={handleEINChange}
            render={modalEINLayout}
            buttonText="Create"
            initialValues={{ country: 'United States' }}
            isConfirmation={false}
          />
        </ContainerHeader>
        <PaginatedTableWithSearch
          enableSearch={true}
          name="Funders"
          endpoint="/funder"
          columns={processedColumns}
          dataOptions={{
            errorMsg: errorMsg('funders'),
          }}
          initialPageSize={30}
          initialSortColumn="name"
          onDataChange={handleDataChange}
        />
      </Card>
    </Container>
  );
};
export default Funders;
